import { IssueStatus, IssueStatusItem } from 'shared'
import WorkflowRollbackModal from 'components/CIMSModal/WorkflowRollbackModal'
import { useIssueTrackerContext } from '../../IssueTrackerContext'
import {
  draftAffectedItems,
  pendingDispositionAffectedItems,
  pendingResponseAffectedItems,
} from './utils'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSComment from 'components/CIMSComment'
import { useRevertIssueMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { simpleRevertNotification } from 'hooks/notificationHandler'
import { useState } from 'react'
import Description from './components/Description'
import Response from './components/Response'
import DispositionNotes from './components/DispositionNotes'
import ClosureNotes from './components/ClosureNotes'
import { useWorkflowRollbackComment } from 'components/CIMSComment/hooks/useWorkflowRollbackComment'
import { dateFormatter } from '../../../../../../../../../utils/dateFormatter'

const RevertModal: React.FC = () => {
  const {
    revertModal: { state, toggle, visible },
    issues,
    relations,
  } = useIssueTrackerContext()
  const [mutating, setMutating] = useState(false)
  const [revert] = useRevertIssueMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      revertIssue: {
        issue: { formattedNumber, status },
      },
    }) => simpleRevertNotification({ name: formattedNumber, status }),
  })
  const commentProps = useWorkflowRollbackComment()
  if (!state) return null
  const { statusId, formattedNumber, closedBy, closedDate } = state
  const hideAtPendingResponse =
    statusId === IssueStatus.Closed.id ||
    statusId === IssueStatus.PendingClosure.id ||
    statusId === IssueStatus.PendingDisposition.id
  const hideAtPendingDisposition =
    statusId === IssueStatus.Closed.id ||
    statusId === IssueStatus.PendingClosure.id
  const hideAtClosed = statusId === IssueStatus.Closed.id
  return (
    <WorkflowRollbackModal
      type="multiple"
      onClose={() => toggle(null)}
      loading={mutating}
      initialFormValues={
        {
          description: state.description,
          response: state.response,
          dispositionNotes: state.dispositionNotes,
          closureNotes: state.closureNotes,
          /**
           * comment and status shouldn't be initialized, but they will be available
           * since they are conditionally rendered
           */
        } as unknown as {
          description: string | null
          response: string | null
          dispositionNotes: string | null
          closureNotes: string | null
          comment: string
          status: IssueStatusItem['name']
        }
      }
      onOk={async ({
        comment,
        status,
        description,
        response,
        dispositionNotes,
        closureNotes,
      }) => {
        const { id } = IssueStatus.fromNameOrFail(status)
        setMutating(true)
        await revert({
          variables: {
            input: {
              comment,
              id: state.id,
              statusId: id,
              description,
              response,
              dispositionNotes,
              closureNotes,
            },
          },
        })
        setMutating(false)
        issues.refetch()
        relations.refetch()
        toggle(null)
      }}
      recordName={formattedNumber}
      formItems={
        <CIMSFormItem {...commentProps}>
          <CIMSComment />
        </CIMSFormItem>
      }
      affectedItems={[
        {
          label: 'Closed By',
          value: closedBy?.displayName ?? closedBy?.mail,
        },
        {
          label: 'Closed Date',
          value: dateFormatter.datetime(closedDate),
        },
      ]}
      parentPositions={{ affectedItems: 'after', formItems: 'after' }}
      statuses={[
        {
          statusName: IssueStatus.Draft.name,
          affectedItems: [
            ...draftAffectedItems(state),
            ...pendingResponseAffectedItems(state),
            ...pendingDispositionAffectedItems(state),
          ],
          formItems: (
            <>
              <Description to={IssueStatus.Draft.id} />
              <Response from={statusId} to={IssueStatus.Draft.id} />
              <DispositionNotes from={statusId} to={IssueStatus.Draft.id} />
              <ClosureNotes {...state} />
            </>
          ),
        },
        {
          statusName: IssueStatus.PendingResponse.name,
          affectedItems: [
            ...draftAffectedItems(state, { hidden: hideAtPendingResponse }),
            ...pendingResponseAffectedItems(state),
            ...pendingDispositionAffectedItems(state),
          ],
          formItems: (
            <>
              <Description to={IssueStatus.PendingResponse.id} />
              <Response from={statusId} to={IssueStatus.PendingResponse.id} />
              <DispositionNotes
                from={statusId}
                to={IssueStatus.PendingResponse.id}
              />
              <ClosureNotes {...state} />
            </>
          ),
          disabled: statusId === IssueStatus.PendingResponse.id,
        },
        {
          statusName: IssueStatus.PendingDisposition.name,
          affectedItems: [
            ...draftAffectedItems(state, { hidden: hideAtPendingDisposition }),
            ...pendingResponseAffectedItems(state, {
              hidden: hideAtPendingDisposition,
            }),
            ...pendingDispositionAffectedItems(state),
          ],
          formItems: (
            <>
              <Description to={IssueStatus.PendingDisposition.id} />
              <Response
                from={statusId}
                to={IssueStatus.PendingDisposition.id}
              />
              <DispositionNotes
                from={statusId}
                to={IssueStatus.PendingDisposition.id}
              />
              <ClosureNotes {...state} />
            </>
          ),
          disabled:
            statusId === IssueStatus.PendingResponse.id ||
            statusId === IssueStatus.PendingDisposition.id,
        },
        {
          statusName: IssueStatus.PendingClosure.name,
          affectedItems: [
            ...draftAffectedItems(state, { hidden: hideAtClosed }),
            ...pendingResponseAffectedItems(state, { hidden: hideAtClosed }),
            ...pendingDispositionAffectedItems(state, { hidden: hideAtClosed }),
          ],
          formItems: (
            <>
              <Description to={IssueStatus.PendingClosure.id} />
              <Response from={statusId} to={IssueStatus.PendingClosure.id} />
              <DispositionNotes
                from={statusId}
                to={IssueStatus.PendingClosure.id}
              />
              <ClosureNotes {...state} />
            </>
          ),
          disabled:
            statusId === IssueStatus.PendingResponse.id ||
            statusId === IssueStatus.PendingDisposition.id ||
            statusId === IssueStatus.PendingClosure.id,
        },
      ]}
      visible={visible}
    />
  )
}

export default RevertModal
